import { Controller } from "@hotwired/stimulus";

/**
 * IntersectionObserver only accepts margin lengths as strings (like CSS). Using a
 * fraction of the scroll root's height makes prefetch feel consistent whether the
 * dropdown is short/tall or the user has large text (unlike a fixed 160px guess).
 */
const PREFETCH_MARGIN_RATIO = 0.35;
const PREFETCH_MARGIN_MIN_PX = 48;
const PREFETCH_MARGIN_MAX_PX = 280;

export default class extends Controller {
  static values = {
    url: String,
    minLength: { type: Number, default: 3 },
    debounceMs: { type: Number, default: 300 },
  };

  static targets = ["input", "dropdown", "results"];

  connect() {
    this.debounceTimer = null;
    this.busy = false;
    this.currentQuery = "";
    this.observer = null;
    this.boundOnDocumentClick = this.onDocumentClick.bind(this);
    this.boundOnKeydown = this.onKeydown.bind(this);
    this.boundOnResultsScroll = this.onResultsScroll.bind(this);

    document.addEventListener("click", this.boundOnDocumentClick);
    document.addEventListener("keydown", this.boundOnKeydown);
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.disconnectObserver();
    this.detachResultsScrollListener();
    document.removeEventListener("click", this.boundOnDocumentClick);
    document.removeEventListener("keydown", this.boundOnKeydown);
  }

  inputTargetConnected() {
    this.inputTarget.addEventListener("input", () => this.onInput());
    this.inputTarget.addEventListener("focus", () => this.onInput());
  }

  resultsTargetConnected() {
    this.resultsTarget.addEventListener("scroll", this.boundOnResultsScroll, {
      passive: true,
    });
  }

  resultsTargetDisconnected() {
    this.detachResultsScrollListener();
  }

  detachResultsScrollListener() {
    if (!this.hasResultsTarget) return;
    this.resultsTarget.removeEventListener("scroll", this.boundOnResultsScroll);
  }

  onInput() {
    const query = (this.inputTarget.value || "").trim();

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    if (query.length < this.minLengthValue) {
      this.clearResults();
      this.closeDropdown();
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchPage({
        query,
        url: this.buildFirstPageUrl(query),
        append: false,
      });
      this.debounceTimer = null;
    }, this.debounceMsValue);
  }

  buildFirstPageUrl(query) {
    const baseUrl = this.urlValue;
    if (!baseUrl) return;

    const join = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${join}q=${encodeURIComponent(query)}`;
  }

  fetchPage({ query, url, append }) {
    if (!url || this.busy) return Promise.resolve();

    this.busy = true;
    this.currentQuery = query;

    return fetch(url, {
      method: "GET",
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    })
      .then((r) => (r.ok ? r.text() : ""))
      .then((html) => {
        if (!html || !(this.hasResultsTarget && this.hasDropdownTarget)) {
          if (!append) {
            this.clearResults();
            this.closeDropdown();
          }
          return;
        }

        if (append) {
          this.appendHtml(html);
        } else {
          this.disconnectObserver();
          this.resultsTarget.innerHTML = html;
        }

        this.openDropdown();
      })
      .catch(() => {
        if (!append) {
          this.clearResults();
          this.closeDropdown();
        }
      })
      .finally(() => {
        this.busy = false;
        if (
          this.hasResultsTarget &&
          this.hasDropdownTarget &&
          !this.dropdownTarget.hidden
        ) {
          this.connectObserver();
          requestAnimationFrame(() => this.checkSentinelVisibility());
        }
      });
  }

  /** Fallback when IntersectionObserver misses (some browsers / scroll roots). */
  onResultsScroll() {
    this.checkSentinelVisibility();
  }

  checkSentinelVisibility() {
    if (
      !this.hasResultsTarget ||
      !this.hasDropdownTarget ||
      this.dropdownTarget.hidden ||
      this.busy
    ) {
      return;
    }

    const root = this.resultsTarget;
    const sentinel = root.querySelector("[data-feed-search-dropdown-sentinel]");
    if (!sentinel) return;

    const nextUrl = sentinel.getAttribute("data-next-url");
    if (!nextUrl) return;

    const q = this.currentQuery;
    if (!q) return;

    const rootRect = root.getBoundingClientRect();
    const senRect = sentinel.getBoundingClientRect();
    const margin = this.prefetchRootMarginBottomPx();
    const visible =
      senRect.top < rootRect.bottom + margin && senRect.bottom > rootRect.top - margin;

    if (!visible) return;

    this.disconnectObserver();
    this.fetchPage({ query: q, url: nextUrl, append: true });
  }

  appendHtml(html) {
    const existingSentinel = this.resultsTarget.querySelector(
      "[data-feed-search-dropdown-sentinel]"
    );
    if (existingSentinel) existingSentinel.remove();

    const tmp = document.createElement("div");
    tmp.innerHTML = html;

    Array.from(tmp.childNodes).forEach((node) => {
      this.resultsTarget.appendChild(node);
    });
  }

  /** Bottom-only expansion of the IO root (string), scaled to scroll area height. */
  prefetchRootMarginBottomPx() {
    if (!this.hasResultsTarget) return PREFETCH_MARGIN_MIN_PX;
    const h = this.resultsTarget.clientHeight;
    return Math.min(
      PREFETCH_MARGIN_MAX_PX,
      Math.max(
        PREFETCH_MARGIN_MIN_PX,
        Math.round(h * PREFETCH_MARGIN_RATIO)
      )
    );
  }

  prefetchRootMarginCss() {
    const px = this.prefetchRootMarginBottomPx();
    return `0px 0px ${px}px 0px`;
  }

  connectObserver() {
    this.disconnectObserver();
    const sentinel = this.resultsTarget.querySelector(
      "[data-feed-search-dropdown-sentinel]"
    );
    if (!sentinel) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || this.busy) return;

          const target = entry.target;
          const nextUrl = target.getAttribute("data-next-url");
          if (!nextUrl) return;

          const q = this.currentQuery;
          if (!q) return;

          this.disconnectObserver();
          this.fetchPage({ query: q, url: nextUrl, append: true });
        });
      },
      {
        root: this.resultsTarget,
        threshold: 0,
        rootMargin: this.prefetchRootMarginCss(),
      }
    );

    this.observer.observe(sentinel);
  }

  disconnectObserver() {
    if (!this.observer) return;
    this.observer.disconnect();
    this.observer = null;
  }

  onDocumentClick(event) {
    if (this.element.contains(event.target)) return;
    this.closeDropdown();
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.closeDropdown();
    }
  }

  openDropdown() {
    this.dropdownTarget.hidden = false;
  }

  closeDropdown() {
    this.dropdownTarget.hidden = true;
    this.disconnectObserver();
  }

  clearResults() {
    if (this.hasResultsTarget) this.resultsTarget.innerHTML = "";
  }
}
